import React, { useState, useEffect, useCallback, useMemo, useRef, RefObject } from 'react';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { fetchCarsDiffThanEntrega, approveCarStatus, updateCarStatus } from '../../../api/carsApi';
import { listWorkSessions } from '../../../api/workSessionsApi';

import { getCar, updateCar, searchCars, filterCarsNonEntrega } from '../../../api/carsApi';
import { CarClass } from '../../../interfaces/Car';
import { RootState } from '../../../store/store';
import {
  setSelectedClasses,
  setSelectedStatuses,
  setSelectedPendingStatuses,
  resetFilters,
  setSearchResults,
  setReduxCurrentPage,
  setLastSearchQuery,
  resetSearchResults,
  setSortOrderRedux
} from '../recepcaoReducer';
import AddCarModal from '../../../components/modal/add-car/AddCarModal';
import EditCarModal from '../../../components/modal/edit-car/EditCarModal';
import StatusModal from '../../../components/modal/status-recepcao/StatusRecepcaoModal';
import ApproveConfirmationModal from '../../../components/modal/approve-confirmation/ApproveConfirmationModal';
import FilterCarModal from '../../../components/modal/filter-car/FilterCarModal';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useGuardedReload } from '../../../hooks/useGuardedReload';
import RecepcaoActionsModal from '../../../components/modal/recepcao-actions-modal/RecepcaoActionsModal';
import MotivoEsteiraModal from '../../../components/modal/motivo-esteira/MotivoEsteiraModal';
import { useIsTi } from '../../../hooks/useUserType';
import { useAcompanhamentoAtivo } from '../../../hooks/useAcompanhamentoAtivo';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { trackChecklistStart } from '../../../api/checklistsApi';
import { checklistTypeLabel } from '../../../types/checklist';
import ArrowDown from '../../../img/arrow-down.svg';
import CarAiro from '../../../img/car-airo.svg';
import CarAssociado from '../../../img/car-associado.svg';
import CarAtual from '../../../img/car-atual.svg';
import CarMobi from '../../../img/car-mobi.svg';
import CarPerdaTotal from '../../../img/car-perda-total.svg';
import CarTerceiro from '../../../img/car-terceiro.svg';
import CarExterno from '../../../img/car-externo.svg';
import CarDiretoria from '../../../img/car-diretoria.svg';
import Moto from '../../../img/moto.svg';
import ApproveStatus from '../../../img/approve-status.svg';
import SearchIcon from '../../../img/search-icon.svg';
import Filter from '../../../img/filter.svg';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { toast } from 'react-toastify';
import { mensagemDeErro, notify } from '../../../shared/feedback/notify';
import { criarShareDoAcompanhamento } from '../../../api/acessoAssociadoApi';
import { abrirWhatsApp, montarMensagemDoAcompanhamento } from '../../../shared/whatsapp';
import './Recepcao.css';
import '../../../shared/feedback/erro-de-tela.css';
import ChecklistModal from '../../../components/modal/checklist/ChecklistModal';
import StartChecklistModal, { TipoServico } from '../../../components/modal/start-checklist/StartChecklistModal';
import { getChecklistSessionsByCar, getLatestChecklistSessionByCar } from '../../../api/checklistsApi';
import { invalidateCarsQueries } from '../../../infrastructure/react-query/invalidateCarsQueries';
import FillFormModal from '../../formularios/modal/FillFormModal';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import { getPendingForms, getFormTemplate } from '../../../api/formsApi';
import { FormTemplate } from '../../formularios/interfaces/forms';
import EntreguesTab from '../../../components/entregues-tab/EntreguesTab';
import { ehFamiliaEntrada, ehFamiliaSaida, tipoDeSaidaDoAtendimento } from '../../../shared/checklist-familias';
import AvisoRascunhoPendente, { type RascunhoParaRetomar } from '../../../components/rascunho/AvisoRascunhoPendente';

interface RecepcaoProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

interface FilterParams {
  carClass?: string | string[];
  carStatus?: string | string[];
  pendingStatus?: string | string[];
  approvalStatus?: string | string[];
  damageType?: string | string[];
}

interface ChecklistFlags {
  hasEntradaCompleted: boolean;
  hasSaidaCompleted: boolean;
  /** Tipo real do checklist da família entrada (ENTRADA ou POS_EVENTO_ENTRADA — at-96). */
  entradaType?: TipoServico;
  /**
   * Tipo real do checklist da família saída (SAIDA ou POS_EVENTO_SAIDA — at-96).
   *
   * Existe pelo mesmo motivo do `entradaType`: a visualização busca a sessão POR TIPO, e pedir
   * `SAIDA` num pós-evento não acha nada — a tela diria "checklist ainda não encontrado" sobre
   * um checklist que existe.
   */
  saidaType?: TipoServico;
}

const DEFAULT_SORT_BY = 'entryDate';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';

const defaultColumnWidths = [
  110, 160, 140, 150, 160
];

const Recepcao: React.FC<RecepcaoProps> = ({ sidebarRef }) => {
  const queryClient = useQueryClient();
  const { labelDe } = useEsteiras(true);
  // TI vê a tela em modo somente leitura — sem cadastrar, editar ou aprovar (at-25).
  const isTi = useIsTi();
  // O botão do acompanhamento só existe com a feature ligada (o TI nunca o recebe).
  const acompanhamentoAtivo = useAcompanhamentoAtivo(!isTi);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [isStartChecklistModalOpen, setIsStartChecklistModalOpen] = useState(false);
  const [checklistCarId, setChecklistCarId] = useState<string | null>(null);
  const [checklistSessionId, setChecklistSessionId] = useState<string | null>(null);
  const [checklistTipoServico, setChecklistTipoServico] = useState<TipoServico>('ENTRADA');
  const [checklistReadOnly, setChecklistReadOnly] = useState(false);
  /**
   * at-96: o que se está iniciando (a vistoria de entrada ou a de saída) e se o atendimento é
   * um retorno de garantia. O TIPO concreto é derivado desses dois no `StartChecklistModal` —
   * antes era escolhido num `select`, o que permitia o par errado em silêncio.
   */
  const [checklistFamilia, setChecklistFamilia] = useState<'ENTRADA' | 'SAIDA'>('ENTRADA');
  const [checklistEhPosEvento, setChecklistEhPosEvento] = useState(false);
  const [cars, setCars] = useState<any[]>([]);
  const [totalCars, setTotalCars] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(DEFAULT_SORT_ORDER);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [carToEdit, setCarToEdit] = useState<any | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [carToUpdateStatus, setCarToUpdateStatus] = useState<string | null>(null);
  const [activeTriggerTemplate, setActiveTriggerTemplate] = useState<FormTemplate | null>(null);
  const [activeTriggerCar, setActiveTriggerCar] = useState<{ id: string; plate: string; model: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [postCreateChecklistPromptOpen, setPostCreateChecklistPromptOpen] = useState(false);
  const [createdCarForChecklistId, setCreatedCarForChecklistId] = useState<string | null>(null);
  /** at-96: o veículo recém-cadastrado é um retorno de garantia? (vem do próprio cadastro). */
  const [createdCarEhPosEvento, setCreatedCarEhPosEvento] = useState(false);
  const [checklistFlagsByCarId, setChecklistFlagsByCarId] = useState<Record<string, ChecklistFlags>>({});
  const [checklistFlagsRefreshKey, setChecklistFlagsRefreshKey] = useState(0);
  const [startChecklistConfirm, setStartChecklistConfirm] = useState<{ carId: string; tipo: TipoServico; plate: string } | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [carToApprove, setCarToApprove] = useState<string | null>(null);
  const [approvalWarning, setApprovalWarning] = useState<string | undefined>(undefined);
  // at-64 (BUG-V81): o modal abre antes de a checagem de horas pendentes responder.
  // Enquanto ela não volta, "Confirmar" fica travado; se ela falhar, o modal diz que não
  // foi possível verificar em vez de deixar o silêncio passar por "não há pendência".
  const [approvalChecking, setApprovalChecking] = useState<boolean>(false);
  const [approvalCheckFailed, setApprovalCheckFailed] = useState<string | undefined>(undefined);
  const [isApproveLoading, setIsApproveLoading] = useState<boolean>(false);
  const availableClasses: CarClass[] = Object.values(CarClass);
  const [isDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isStatusSidebarOpen] = useState<boolean>(true);
  const startCarIndex = (currentPage - 1) * limit + 1;
  const endCarIndex = Math.min(currentPage * limit, totalCars);
  const dispatch = useDispatch();
  const [actionsModalOpen, setActionsModalOpen] = useState<boolean>(false);
  const [selectedCarForActions, setSelectedCarForActions] = useState<string | null>(null);
  // at-89: veículo cujo motivo da pendência está sendo editado.
  const [carroParaMotivo, setCarroParaMotivo] = useState<string | null>(null);
  // at-126: a ação "Compartilhar Acompanhamento" está gerando o link.
  const [compartilhandoAcompanhamento, setCompartilhandoAcompanhamento] = useState(false);

  /**
   * at-126: abre o WhatsApp com o link da página de acompanhamento. Sem telefone, o backend
   * recusa (`CAR_WITHOUT_OWNER_PHONE`) com a mensagem que manda cadastrar na edição do veículo —
   * o motivo vai ao toast, e o WhatsApp NÃO abre: abrir sem link faria o atendente acreditar
   * que enviou.
   */
  const handleCompartilharAcompanhamento = async (carId: string) => {
    setCompartilhandoAcompanhamento(true);
    try {
      const share = await criarShareDoAcompanhamento(carId);
      abrirWhatsApp(share.ownerPhone, montarMensagemDoAcompanhamento(share));
      setActionsModalOpen(false);
      setSelectedCarForActions(null);
    } catch (error) {
      notify.actionError(error, { action: 'send', subject: 'o link do acompanhamento' });
    } finally {
      setCompartilhandoAcompanhamento(false);
    }
  };
  const [activeTab, setActiveTab] = useState<'ATIVOS' | 'ENTREGUES'>('ATIVOS');

  const normalizeStatusKey = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');

  /** Abre a confirmação de início de checklist (o início é rastreado em auditoria). */
  const requestStartChecklist = async (carId: string, tipo: TipoServico) => {
    let plate = (cars as any[]).find((c: any) => c?.id === carId)?.plate;
    if (!plate) {
      try {
        plate = (await getCar(carId))?.plate;
      } catch {
        // A placa é só o rótulo da confirmação, então o fluxo segue sem ela — mas a falha da
        // consulta não pode ficar invisível (at-60).
        notify.warning(
          'Não foi possível carregar os dados deste veículo. Confira a placa antes de confirmar.',
          { toastId: 'recepcao-placa-confirmacao' },
        );
      }
    }
    setStartChecklistConfirm({ carId, tipo, plate: plate || '' });
  };

  const confirmStartChecklist = async () => {
    if (!startChecklistConfirm) return;
    const { carId, tipo } = startChecklistConfirm;
    setStartChecklistConfirm(null);

    // SAÍDA exige entrada concluída: sem ela, não abre o modal nem registra início
    if (ehFamiliaSaida(tipo)) {
      try {
        const sessions = await getChecklistSessionsByCar(carId);
        const temEntrada = (sessions || []).some((s: any) => ehFamiliaEntrada(s?.type));
        if (!temEntrada) {
          toast.error('O checklist de saída só pode ser iniciado após a conclusão do checklist de entrada deste veículo.');
          return;
        }
      } catch {
        // O backend ainda bloqueia o track-start e a conclusão; o aviso existe para quem
        // preenche não descobrir a exigência só no fim do checklist (at-60).
        notify.warning(
          'Não foi possível confirmar se o checklist de entrada deste veículo já foi concluído. Se ele ainda não existir, a conclusão do checklist de saída será recusada.',
          { toastId: 'recepcao-checagem-entrada' },
        );
      }
    }

    try {
      await trackChecklistStart(carId, tipo);
    } catch {
      // O rastreio de auditoria não bloqueia o preenchimento, mas falhar aqui costuma indicar
      // permissão ou conexão com problema — o mesmo que impediria a conclusão adiante (at-60).
      notify.warning(
        'O início deste checklist não pôde ser registrado. O preenchimento segue normalmente; se a conclusão também falhar, acione a TI.',
        { toastId: 'recepcao-inicio-checklist' },
      );
    }

    setChecklistCarId(carId);
    setChecklistSessionId(null);
    setChecklistTipoServico(tipo);
    setChecklistReadOnly(false);
    setIsChecklistModalOpen(true);
  };

  const openChecklistReadOnly = async (carId: string, checklistType?: TipoServico) => {
    try {
      let targetSession: any;

      if (checklistType) {
        try {
          targetSession = await getLatestChecklistSessionByCar(carId, {
            type: checklistType,
            status: 'COMPLETED',
          });
        } catch {
          const sessions = await getChecklistSessionsByCar(carId);
          const typedSessions = (sessions || []).filter(
            (session: any) =>
              normalizeStatusKey(session?.type) === checklistType &&
              normalizeStatusKey(session?.status) === 'COMPLETED'
          );
          targetSession = typedSessions[0];
        }
      } else {
        targetSession = await getLatestChecklistSessionByCar(carId);
      }

      if (!targetSession?.id) {
        throw new Error(
          checklistType
            ? `Checklist de ${ehFamiliaSaida(checklistType) ? 'saída' : 'entrada'} ainda não encontrado para este veículo.`
            : 'Checklist ainda não encontrado para este veículo.'
        );
      }

      setChecklistCarId(carId);
      setChecklistSessionId(targetSession.id);
      setChecklistTipoServico((targetSession.type || checklistType || 'ENTRADA') as TipoServico);
      setChecklistReadOnly(true);
      setIsChecklistModalOpen(true);
    } catch (e: any) {
      toast.error(
        e?.message ||
        (checklistType
          ? `Checklist de ${ehFamiliaSaida(checklistType) ? 'saída' : 'entrada'} ainda não encontrado para este veículo.`
          : 'Checklist ainda não encontrado para este veículo.')
      );
    }
  };

  const loadCars = async (page: number, limit: number, sortBy: string = DEFAULT_SORT_BY, sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER) => {
    setLoading(true);
    try {
      const response = await fetchCarsDiffThanEntrega(page, limit, sortBy, sortOrder);
      const filteredTotal = response.total;
      setCars(response.cars);
      setTotalCars(filteredTotal);
      setTotalPages(Math.ceil(filteredTotal / limit));
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (
    page: number = 1,
    sortBy: string = DEFAULT_SORT_BY,
    sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    handleResetFilters();
    try {
      const response = await searchCars(
        searchQuery,
        page,
        limit,
        sortBy,
        sortOrder
      );

      dispatch(
        setSearchResults({
          page,
          cars: response.cars,
          totalItems: response.total,
          totalPages: Math.ceil(response.total / limit),
        })
      );
      dispatch(setReduxCurrentPage(page));
      dispatch(setLastSearchQuery(searchQuery));

      setCars(response.cars);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (err) {
      setError('Erro ao realizar a pesquisa');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRascunhosVersao((v) => v + 1);
    loadCars(currentPage, limit);
  };

  // oficina/at-136: retomar o cadastro ou o checklist guardado como rascunho. O checklist já teve
  // o início registrado quando foi aberto da primeira vez — retomar não registra de novo.
  const [rascunhosVersao, setRascunhosVersao] = useState(0);
  const retomarRascunho = (r: RascunhoParaRetomar) => {
    if (r.tipo === 'CADASTRO') {
      setIsModalOpen(true);
      return;
    }
    setChecklistCarId(r.carId);
    setChecklistSessionId(null);
    setChecklistTipoServico(r.checklistType as TipoServico);
    setChecklistReadOnly(false);
    setIsChecklistModalOpen(true);
  };

  // at-65 (BUG-V73): `handleOpenDetailsModal`/`handleCloseDetailsModal` e o `CarDetailsModal`
  // desta tela foram removidos — o único caminho até eles era a prop `onViewDetails`, que o
  // `RecepcaoActionsModal` recebia e nunca chamava. Removido, não comentado (regra de
  // 02/08/2026). Se a Recepção **deve** ter "Ver detalhes", é feature a decidir, não este
  // código morto de volta.

  const handleOpenEditModal = async (carId: string) => {
    try {
      const details = await getCar(carId);
      setCarToEdit(details);
      setIsEditModalOpen(true);
    } catch (err) {
      setError('Erro ao carregar detalhes do carro para edição');
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCarToEdit(null);
  };

  const handleApproveStatus = async (carId: string) => {
    try {
      await approveCarStatus(carId);
      await invalidateCarsQueries(queryClient);
      notify.success('Esteira aprovada com sucesso.');
      loadCars(currentPage, limit);

      // CHECK FOR TRIGGERED FORMS
      try {
        const pendingForms = await getPendingForms();
        const match = pendingForms.find((item) => item.car.id === carId);
        if (match) {
          const template = await getFormTemplate(match.templateId);
          setActiveTriggerCar(match.car);
          setActiveTriggerTemplate(template);
        }
      } catch {
        // A aprovação já foi concluída — só a checagem de formulários disparados falhou (at-60).
        notify.warning(
          'A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.',
          { toastId: 'recepcao-formularios-pos-aprovacao' },
        );
      }
    } catch (error) {
      notify.actionError(error, { action: 'approve', subject: 'esteira' });
    }
  };

  const openConfirmationModal = async (carId: string) => {
    setCarToApprove(carId);
    setApprovalWarning(undefined);
    setApprovalCheckFailed(undefined);
    // Trava o "Confirmar" ANTES de abrir: entre a abertura e a resposta da checagem havia
    // uma janela em que dava para confirmar sem nunca ver o aviso de horas pendentes.
    setApprovalChecking(true);
    setConfirmationModalOpen(true);
    try {
      const res = await listWorkSessions({ carId, approvalStatus: 'PENDING', limit: 1 });
      if (res.sessions.length > 0) {
        setApprovalWarning('Este veículo possui horas de atividade pendentes de aprovação pelo Chefe de Oficina. Aguarde a aprovação das horas antes de alterar a esteira.');
      }
    } catch (error) {
      // O modal continua abrindo, mas sem o aviso de horas pendentes seria possível aprovar a
      // esteira achando que não há nada em aberto (at-60).
      // at-64: o toast some e o modal fica na tela — o aviso precisa estar DENTRO dele.
      // Não bloqueia a aprovação (falha de consulta não pode parar a oficina); apenas
      // impede que o silêncio seja lido como "não há pendência".
      setApprovalCheckFailed(
        'Não foi possível verificar se este veículo tem horas pendentes de aprovação. Confirme apenas se tiver certeza; se preferir, cancele e recarregue a página.',
      );
      notify.errorFrom(
        error,
        'Não foi possível carregar a situação das horas pendentes deste veículo. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setApprovalChecking(false);
    }
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const confirmApproveStatus = async () => {
    if (carToApprove) {
      setIsApproveLoading(true);
      try {
        await handleApproveStatus(carToApprove);
        setActionsModalOpen(false);
      } finally {
        setIsApproveLoading(false);
        setConfirmationModalOpen(false);
        setCarToApprove(null);
      }
    } else {
      setConfirmationModalOpen(false);
    }
  };

  const handleOpenStatusModal = (carId: string) => {
    setCarToUpdateStatus(carId);
    setStatusModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (carToUpdateStatus && selectedStatus) {
      const targetCarId = carToUpdateStatus;
      try {
        await updateCarStatus(targetCarId, selectedStatus);
        await invalidateCarsQueries(queryClient);
        notify.success('Esteira atualizada com sucesso.');
        loadCars(currentPage, limit);
        setStatusModalOpen(false);

        // CHECK FOR TRIGGERED FORMS
        try {
          const pendingForms = await getPendingForms();
          const match = pendingForms.find((item) => item.car.id === targetCarId);
          if (match) {
            const template = await getFormTemplate(match.templateId);
            setActiveTriggerCar(match.car);
            setActiveTriggerTemplate(template);
          }
        } catch {
          // A esteira já foi atualizada — só a checagem de formulários disparados falhou (at-60).
          notify.warning(
            'A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.',
            { toastId: 'recepcao-formularios-pos-esteira' },
          );
        }

        setSelectedStatus(null);
        setCarToUpdateStatus(null);
      } catch (error) {
        notify.actionError(error, { action: 'update', subject: 'status' });
      }
    }
  };

  const handleApplyFilters = async (filters: any) => {
    dispatch(resetSearchResults());
    dispatch(setSelectedClasses(filters.carClass || []));
    dispatch(setSelectedStatuses(filters.carStatus || []));
    dispatch(setSelectedPendingStatuses(filters.pendingStatus || []));

    // at-65 (BUG-V44): volta para a página 1. Aplicar filtro estando na página 3 pedia a
    // página 3 do resultado novo — que quase sempre não existe, e a tela ficava vazia sem
    // explicação (mesma regra já adotada na tela de Usuários, at-61).
    setCurrentPage(1);

    setLoading(true);
    try {
      const response = await filterCarsNonEntrega(
        filters.carClass,
        filters.carStatus,
        filters.pendingStatus,
        filters.approvalStatus,
        filters.damageType,
        1,
        limit,
        sortBy ?? DEFAULT_SORT_BY,
        sortOrder ?? DEFAULT_SORT_ORDER
      );

      setCars(response.cars);
      setTotalPages(Math.ceil(response.total / limit));
      // at-65 (BUG-V44): o contador de itens ("X-Y de Z veículos") ficava com o total da
      // consulta ANTERIOR — e, com os filtros fora da tela, o contador é o único indício
      // de que a lista está filtrada.
      setTotalCars(response.total);
    } catch (err) {
      setCars([]);
      setTotalCars(0);
      setError(mensagemDeErro(err, 'Não foi possível aplicar os filtros. Recarregue a página; se persistir, acione a TI.'));
      notify.errorFrom(err, 'Não foi possível aplicar os filtros. Recarregue a página; se persistir, acione a TI.');
    } finally {
      setLoading(false);
    }

    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const selectedClasses = useSelector((state: RootState) => state.recepcao.selectedClasses);
  const selectedStatuses = useSelector((state: RootState) => state.recepcao.selectedStatuses);
  const selectedPendingStatuses = useSelector((state: RootState) => state.recepcao.selectedPendingStatuses);
  const selectedApprovalStatus = useSelector((state: RootState) => state.recepcao.selectedApprovalStatus);
  const selectedDamageType = useSelector((state: RootState) => state.recepcao.selectedDamageType);

  const appliedFilters = {
    carClass: selectedClasses || [],
    carStatus: selectedStatuses || [],
    pendingStatus: selectedPendingStatuses || [],
    approvalStatus: selectedApprovalStatus || undefined,
    damageType: selectedDamageType || [],
  };

  const loadCarsWithFilters = async (
    recepcao: FilterParams,
    page: number,
    limit: number,
    sortBy: string = DEFAULT_SORT_BY,
    sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    try {
      const response = await filterCarsNonEntrega(
        recepcao.carClass,
        recepcao.carStatus,
        recepcao.pendingStatus,
        recepcao.approvalStatus,
        recepcao.damageType,
        page,
        limit,
        sortBy,
        sortOrder
      );

      const filteredTotal = response.total;
      setCars(response.cars);
      setTotalPages(Math.ceil(filteredTotal / limit));
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (selectedSortBy: string, order: 'asc' | 'desc') => {
    dispatch(setSortOrderRedux({ sortBy: selectedSortBy, sortOrder: order }));
    setSortBy(selectedSortBy);
    setSortOrder(order);
    setIsFilterDropdownOpen(false);
  };

  const handleClickOutsideFilterDropdown = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsFilterDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideFilterDropdown);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    let isMounted = true;

    const normalizeKey = (value: unknown) =>
      String(value ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');

    /**
     * Situação de checklist do veículo SELECIONADO (at-65, BUG-V36).
     *
     * Antes era **uma consulta por veículo da página** (`getChecklistSessionsByCar` em
     * `Promise.all` sobre `cars`), refeita a cada troca de página, filtro e ordenação —
     * para alimentar botões de um modal que abre **um** veículo por vez. Agora a consulta
     * acontece na abertura do modal de ações, que é onde as flags são lidas.
     */
    const loadChecklistFlags = async () => {
      if (!selectedCarForActions) return;

      try {
        const sessions = await getChecklistSessionsByCar(selectedCarForActions);
        if (!isMounted) return;

        const normalizedSessions = (sessions || []).map((session: any) => ({
          type: normalizeKey(session?.type),
          status: normalizeKey(session?.status),
        }));

        // at-96: por FAMÍLIA. Com `=== 'SAIDA'`, a saída de um pós-evento não seria reconhecida
        // e a tela ofereceria "fazer checklist de saída" num atendimento que já saiu.
        const entradaSession = normalizedSessions.find(
          (session: any) => ehFamiliaEntrada(session.type) && session.status === 'COMPLETED'
        );
        const saidaSession = normalizedSessions.find(
          (session: any) => ehFamiliaSaida(session.type) && session.status === 'COMPLETED'
        );

        setChecklistFlagsByCarId((prev) => ({
          ...prev,
          [selectedCarForActions]: {
            hasEntradaCompleted: Boolean(entradaSession),
            hasSaidaCompleted: Boolean(saidaSession),
            entradaType: entradaSession?.type as TipoServico | undefined,
            saidaType: saidaSession?.type as TipoServico | undefined,
          },
        }));
      } catch (e) {
        if (!isMounted) return;
        // Sem a consulta, o veículo cai no mesmo estado de quem não tem checklist —
        // por isso a falha precisa ser dita ao operador (at-60).
        setChecklistFlagsByCarId((prev) => ({
          ...prev,
          [selectedCarForActions]: { hasEntradaCompleted: false, hasSaidaCompleted: false },
        }));
        // ⚠️ `notify.error` com texto fixo, NÃO `errorFrom`: num 403 o extrator devolveria
        // "Você não tem permissão para executar esta ação" e engoliria o que importa aqui —
        // a CONSEQUÊNCIA ("aparece como se não tivesse checklist; confira antes de iniciar
        // nova vistoria"). O motivo HTTP não orienta; a consequência sim (at-60/at-65).
        notify.error(
          'Não foi possível carregar a situação dos checklists deste veículo. Ele aparece como se ainda não tivesse checklist, o que pode não ser verdade — confira antes de iniciar uma nova vistoria. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'recepcao-flags-checklist' },
        );
      }
    };

    void loadChecklistFlags();

    return () => {
      isMounted = false;
    };
    // Roda na seleção do veículo e quando o refresh é pedido explicitamente (depois de
    // concluir um checklist, via `checklistFlagsRefreshKey`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarForActions, checklistFlagsRefreshKey]);



  // Recarrega a lista só quando filtros/página/limite/ordenação mudam de fato — a comparação
  // com o estado anterior vive no useGuardedReload (at-43); a decisão de COMO recarregar
  // (busca por texto × filtros × listagem) continua aqui.
  useGuardedReload({
    filters: appliedFilters,
    page: currentPage,
    limit,
    sortBy,
    sortOrder,
    searchQuery,
    onReload: ({ hasSortChanged }) => {
      setLoading(true);

      const someValidFilter = Object.values(appliedFilters).some((val) => val && val.length > 0);

      const safeSortBy = sortBy ?? DEFAULT_SORT_BY;
      const safeSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

      if (searchQuery) {
        handleSearch(currentPage, safeSortBy, safeSortOrder);
      } else if (someValidFilter || hasSortChanged) {
        loadCarsWithFilters(
          appliedFilters,
          currentPage,
          limit,
          safeSortBy,
          safeSortOrder
        );
      } else {
        loadCars(currentPage, limit, safeSortBy, safeSortOrder);
      }
    },
    onSkip: () => setLoading(false),
  });

  const headers = [
    'Placa',
    'Modelo',
    'Classe',
    'Esteira atual',
    'Esteira pendente',
  ];

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  // `labelDe` é estável (useCallback no useEsteiras), então as dependências fecham.
  const getColumnValues = useCallback(
    (car: any) => [
      car.plate || '-',
      car.model || '-',
      car.carClass || '-',
      labelDe(car.status),
      labelDe(car.pendingStatus),
    ],
    [labelDe],
  );

  const tableRows = useMemo(() => cars.map(getColumnValues), [cars, getColumnValues]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
    // A tabela de ativos só existe no DOM quando a aba "Em atendimento" está visível
    enabled: activeTab === 'ATIVOS',
  });

  const showAtivosLoading = activeTab === 'ATIVOS' && loading;

  const tabsRow = (
    <div className="recepcao-tabs">
      <button
        type="button"
        className={`recepcao-tab ${activeTab === 'ATIVOS' ? 'active' : ''}`}
        onClick={() => setActiveTab('ATIVOS')}
      >
        Em atendimento
      </button>
      <button
        type="button"
        className={`recepcao-tab ${activeTab === 'ENTREGUES' ? 'active' : ''}`}
        onClick={() => setActiveTab('ENTREGUES')}
      >
        Entregues
      </button>
    </div>
  );

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          A Recepção acompanha os atendimentos da oficina em duas abas: <strong>Em atendimento</strong> (veículos que
          estão na oficina) e <strong>Entregues</strong> (atendimentos encerrados). Cada linha é um atendimento — se o
          mesmo veículo voltar à oficina no futuro, será uma nova linha. Atenção ao combinar busca e filtros:{' '}
          <strong>pesquisar limpa os filtros aplicados</strong>, e aplicar filtros limpa a pesquisa — eles não
          funcionam juntos.
        </p>
      ),
    },
    {
      titulo: 'Cadastrar um veículo',
      conteudo: (
        <>
          <p>
            Em <strong>+ Adicionar veículo</strong>, o sistema pergunta o caminho: <strong>Cadastrar veículo com
            evento</strong> (digite o número do evento aberto no SGA para ver todos os veículos envolvidos —
            associado e terceiros — e clicar no que está entrando; o formulário é preenchido com os dados dele, nada
            é preenchido antes da sua escolha), <strong>Cadastrar veículo sem evento</strong> (o vínculo com o
            evento pode ser feito depois, pela edição) ou <strong>Cadastrar pós-evento (retorno de
            garantia)</strong>.
          </p>
          <p>
            Somente <strong>placa</strong> e <strong>classe</strong> são obrigatórias — os demais campos podem ser
            completados depois. A exceção é o veículo de <strong>associado</strong>, que exige o número do evento, e
            cada evento aceita apenas um veículo de associado.
          </p>
          <p>
            Se a placa já tiver um atendimento em andamento, o cadastro é bloqueado — um veículo só pode ter um
            atendimento ativo por vez. Se a consulta ao SGA falhar, o cadastro segue normalmente com preenchimento
            manual.
          </p>
          <p>
            Confira o <strong>Telefone do proprietário</strong>: é ele que o cliente usa para acessar o checklist
            e o acompanhamento do veículo. O acompanhamento segue junto na mensagem de WhatsApp do checklist de
            entrada e também pode ser enviado sozinho, pela ação <strong>Compartilhar Acompanhamento</strong> no
            modal da placa. Terceiro sem telefone cadastrado não recebe nenhum dos dois.
          </p>
          <p>
            O próprio modal de cadastro tem um <strong>?</strong> ao lado do título, com a explicação completa dos
            três caminhos e dos dois números de evento do pós-evento.
          </p>
        </>
      ),
    },
    {
      titulo: 'Pós-evento (retorno de garantia)',
      conteudo: (
        <>
          <p>
            Quando o veículo já passou pela oficina, foi entregue e <strong>volta para avaliação do serviço</strong>{' '}
            ou correção, o cadastro certo é o de <strong>pós-evento</strong>. Ele é um atendimento novo — com número
            de evento <strong>próprio</strong>, aberto no SGA como qualquer outro — e fica <strong>vinculado</strong>{' '}
            ao serviço original.
          </p>
          <p>
            São <strong>dois números de evento</strong> nesse caminho, e eles não se misturam: o do{' '}
            <strong>serviço original</strong> serve só para achar o atendimento anterior (a tela lista os veículos já
            entregues daquele evento, com a data de entrega), e o <strong>evento deste pós-evento</strong> é o número
            novo. Quando o número digitado no cadastro com evento já tem veículo entregue, a tela avisa e oferece
            trocar para este caminho.
          </p>
          <p>
            O sistema <strong>não valida prazo de garantia</strong>: a data de entrega e há quantos dias o veículo
            saiu são exibidas apenas como informação. O atendimento de pós-evento segue todas as regras dos demais —
            inclusive a <strong>liberação administrativa</strong>, que continua sendo exigida.
          </p>
          <p>
            O veículo aparece marcado com o selo <strong>Pós-evento</strong> ao lado da placa, e o checklist dele é o
            de pós-evento: o tipo acompanha o atendimento e não é escolhido na hora de iniciar.
          </p>
        </>
      ),
    },
    {
      titulo: 'Checklists de entrada e saída',
      conteudo: (
        <>
          <p>
            O checklist de <strong>Entrada</strong> é a vistoria de chegada: cada atendimento tem no máximo um, e
            concluí-lo <strong>não</strong> movimenta o veículo — quem define a etapa seguinte é o Chefe de Oficina.
            Num atendimento de pós-evento os tipos são os de <strong>pós-evento</strong>, com os mesmos itens; o tipo
            acompanha o atendimento e não é escolhido.
          </p>
          <p>
            O checklist de <strong>Saída</strong> só pode ser iniciado depois que o de entrada estiver concluído, e{' '}
            <strong>concluí-lo encerra o atendimento</strong>: o veículo vai para a etapa de entrega e sai da lista
            "Em atendimento".
          </p>
          <p>
            Fotos e vídeos nunca são obrigatórios — o sistema apenas confirma se você quer salvar sem fotos. Não há
            limite de quantidade de imagens.
          </p>
        </>
      ),
    },
    {
      titulo: 'Esteira pendente e aprovação',
      conteudo: (
        <p>
          Quando um colaborador sugere a próxima etapa, ela aparece na coluna <strong>Esteira pendente</strong> com
          um botão de aprovação. A aprovação fica <strong>bloqueada enquanto houver horas de atividade aguardando a
          aprovação do Chefe de Oficina</strong> — o aviso aparece na confirmação. Também é possível solicitar a
          mudança pela ação <strong>Solicitar Mudança de Esteira</strong> no modal da placa; mover para a entrega por
          esse caminho exige o checklist de saída concluído.
        </p>
      ),
    },
    {
      titulo: 'Aba Entregues',
      conteudo: (
        <>
          <p>
            Os atendimentos encerrados saem da lista principal, mas continuam acessíveis em <strong>Entregues</strong>:
            o clique na placa abre os dados e os checklists em modo de consulta, com <strong>PDF</strong> e{' '}
            <strong>compartilhamento por WhatsApp</strong> (que exige o telefone do proprietário cadastrado). Pela
            placa também ficam <strong>Editar Carro</strong> (para corrigir o cadastro, como o telefone) e{' '}
            <strong>Enviar Pesquisa de Satisfação</strong>.
            Ali também fica <strong>Compartilhar Acompanhamento</strong>, que continua disponível por 30 dias
            depois da entrega: é o prazo em que o associado ainda abre a página do acompanhamento.
          </p>
          <p>
            A coluna <strong>Pesquisa</strong> mostra se a pesquisa de satisfação já foi enviada:{' '}
            <strong>Não enviada</strong>, <strong>Enviada em</strong> (o associado ainda não respondeu),{' '}
            <strong>Respondida em</strong> ou <strong>Sem telefone</strong> (cadastre o telefone em Editar Carro para
            poder enviar). Marque <strong>Só sem pesquisa enviada</strong> para ver só os que ainda faltam. O envio
            continua sendo feito por você: o sistema não manda a pesquisa sozinho.
          </p>
        </>
      ),
    },
    ...(isTi
      ? [
          {
            titulo: 'Seu acesso (TI)',
            conteudo: (
              <p>
                O perfil de TI vê esta tela em <strong>modo somente leitura</strong>: cadastrar, editar, aprovar
                esteira e preencher checklist ficam com a Recepção. Você pode consultar os atendimentos e visualizar
                os checklists realizados.
              </p>
            ),
          },
        ]
      : []),
  ];

  const helpButton = (
    <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
      ?
    </button>
  );

  return (
    <div
      className="Frame1000004012"
      style={{
        height: showAtivosLoading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: showAtivosLoading ? 'center' : 'space-between',
        alignItems: showAtivosLoading ? 'center' : 'flex-start',
        backgroundColor: showAtivosLoading ? '#808080' : 'transparent',
      }}
    >
      {showAtivosLoading && <FullScreenLoader />}
      {activeTab === 'ENTREGUES' && (
        <div className="ConteDo recepcao-conte-do">
          <EntreguesTab sidebarRef={sidebarRef} tabs={tabsRow} titleExtra={helpButton} />
        </div>
      )}

      {activeTab === 'ATIVOS' && !loading && (
        <div className="ConteDo recepcao-conte-do">
          {/* at-65: a faixa fica DENTRO do conteúdo; o `!error` apagava abas, busca e tabela. */}
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}
          {!isTi && <AvisoRascunhoPendente versao={rascunhosVersao} onRetomar={retomarRascunho} />}
          <div className="recepcao-header-row">
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Recepção
              {helpButton}
            </div>
            <div className="recepcao-search-bar">
              <div className="ItemSearch" onClick={() => handleSearch()}>
                <img
                  className="imgIcon"
                  src={SearchIcon}
                  alt="Ícone de busca"
                  width="20"
                  height="20"
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Pesquise por placa, responsável ou nº do evento"
                className="recepcao-search-input"
              />
            </div>
          </div>
          {tabsRow}
          <div className="VisualizarOrdenar">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                  />
                  <div className="ComponentName">Filtrar</div>
                </div>
              </div>
              <div className="CtaBig MouseHover" onClick={() => setIsFilterDropdownOpen(!isDropdownOpen)}>
                <div className="Frame1000003987">
                  <img
                    src={ArrowDown}
                    alt="Filtro"
                    width="12.80"
                    height="12.80"
                  />
                  <div className="ComponentName">Ordenar por</div>
                </div>
                {isDropdownOpen && (
                  <div className="DropdownMenu" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('plate', 'asc');
                      }}
                    >
                      Placa
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'desc');
                      }}
                    >
                      Data de entrada mais recente
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'asc');
                      }}
                    >
                      Data de entrada mais antiga
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!isTi && (
              <div onClick={handleOpenModal} className="CtaSmall MouseHover">
                <div className="ComponentName">+ Adicionar veículo</div>
              </div>
            )}
          </div>
          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {loading ? (
                      'Carregando...'
                    ) : error ? (
                      error
                    ) : (
                      `${startCarIndex}-${endCarIndex} de ${totalCars} carros`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 20,
            }}
          >
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header, index) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {cars.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                      <div className="Data">Nenhum carro encontrado.</div>
                    </div>
                  </div>
                ) : (
                  cars.map((car) => {
                    return (
                      <div key={car.id} className="Tabela">
                        <div className="Item">
                          <div
                            className="Data"
                            onClick={() => {
                              setSelectedCarForActions(car.id);
                              setActionsModalOpen(true);
                            }}
                            style={{
                              cursor: 'pointer',
                              color: '#ea5d0e',
                              fontWeight: '600',
                              textDecoration: 'underline',
                            }}
                          >
                            {car.plate || '-'}
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.model || '-'}</div>
                        </div>

                        <div className="Item">
                          <div
                            className="Data"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            {car.carClass === 'Airo Clube' && (
                              <img src={CarAiro} alt="Car Airo" width="15" height="15" />
                            )}
                            {car.carClass === 'Associado' && (
                              <img src={CarAssociado} alt="Car Associado" width="15" height="15" />
                            )}
                            {car.carClass === 'Carros Atual' && (
                              <img src={CarAtual} alt="Car Atual" width="15" height="15" />
                            )}
                            {car.carClass === 'Mobi' && (
                              <img src={CarMobi} alt="Car Mobi" width="15" height="15" />
                            )}
                            {car.carClass === 'Perda Total' && (
                              <img src={CarPerdaTotal} alt="Car Perda Total" width="15" height="15" />
                            )}
                            {car.carClass === 'Terceiro' && (
                              <img src={CarTerceiro} alt="Car Terceiro" width="15" height="15" />
                            )}
                            {car.carClass === 'Carros Externos' && (
                              <img src={CarExterno} alt="Car Externo" width="15" height="15" />
                            )}
                            {car.carClass === 'Moto' && (
                              <img src={Moto} alt="Moto" width="15" height="15" />
                            )}
                            {car.carClass === 'Diretoria' && (
                              <img src={CarDiretoria} alt="Car Diretoria" width="15" height="15" />
                            )}
                            <span>{car.carClass || '-'}</span>
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.status ? labelDe(car.status) : 'Fazer Checklist'}</div>
                        </div>

                        <div className="Item">
                          <div className="ChefeDeOficinaPendingCell">
                            <span
                              className="Data"
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                display: 'inline-block',
                              }}
                            >
                              {labelDe(car.pendingStatus)}
                            </span>

                            {car.pendingStatus && !isTi && (
                              <button
                                type="button"
                                className="ChefeDeOficinaIconButton"
                                onClick={() => openConfirmationModal(car.id)}
                                title="Aprovar esteira pendente"
                                aria-label="Aprovar esteira pendente"
                              >
                                <img
                                  className="ChefeDeOficinaIcon"
                                  src={ApproveStatus}
                                  alt=""
                                />
                              </button>
                            )}
                          </div>
                        </div>


                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <Paginacao
              paginaAtual={currentPage}
              totalPaginas={totalPages}
              onMudarPagina={setCurrentPage}
            />
          </div>
        </div>
      )}
      <AddCarModal
        isOpen={isModalOpen}
        onCancel={handleCloseModal}
        onCarCreated={(carId: string, ehPosEvento?: boolean) => {
          setIsModalOpen(false);
          setRascunhosVersao((v) => v + 1);
          setCreatedCarForChecklistId(carId);
          // at-96: o veículo recém-cadastrado pode ser um retorno de garantia — e é o próprio
          // cadastro que sabe disso. Sem carregar o dado até aqui, o checklist iniciado logo em
          // seguida sairia com o tipo errado e o backend o recusaria.
          setCreatedCarEhPosEvento(Boolean(ehPosEvento));
          setPostCreateChecklistPromptOpen(true);
          loadCars(currentPage, limit);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />
      <EditCarModal
        isOpen={isEditModalOpen}
        onCancel={handleCloseEditModal}
        carDetails={carToEdit}
        onUpdate={async (id: string, data: any) => {
          try {
            await updateCar(id, data);
            loadCars(currentPage, limit);
          } catch (err) {
            throw err;
          }
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />
      <StatusModal
        isOpen={statusModalOpen}
        carId={carToUpdateStatus}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onConfirm={handleUpdateStatus}
        onCancel={() => {
          setStatusModalOpen(false);
          setSelectedStatus(null);
          setCarToUpdateStatus(null);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />
      <ApproveConfirmationModal
        isOpen={confirmationModalOpen}
        isLoading={isApproveLoading}
        message={(() => {
          const car = cars.find((c) => c.id === carToApprove);
          const destino = car?.pendingEsteira?.nome || car?.pendingStatusLabel || car?.pendingStatus;
          return destino
            ? `Tem certeza de que deseja aprovar a mudança deste veículo para a esteira "${destino}"?`
            : 'Tem certeza de que deseja aprovar a esteira deste veículo?';
        })()}
        onConfirm={confirmApproveStatus}
        warningMessage={approvalWarning}
        isVerifying={approvalChecking}
        infoMessage={approvalCheckFailed}
        onCancel={() => {
          setConfirmationModalOpen(false);
          setCarToApprove(null);
          setApprovalWarning(undefined);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />
      <ApproveConfirmationModal
        isOpen={postCreateChecklistPromptOpen}
        message="Veículo salvo com sucesso. Deseja iniciar o checklist agora?"
        onConfirm={() => {
          if (!createdCarForChecklistId) {
            setPostCreateChecklistPromptOpen(false);
            return;
          }

          setChecklistCarId(createdCarForChecklistId);
          setChecklistSessionId(null);
          setChecklistReadOnly(false);
          setChecklistFamilia('ENTRADA');
          setChecklistEhPosEvento(createdCarEhPosEvento);
          setPostCreateChecklistPromptOpen(false);
          setCreatedCarForChecklistId(null);
          setIsStartChecklistModalOpen(true);
        }}
        onCancel={() => {
          setPostCreateChecklistPromptOpen(false);
          setCreatedCarForChecklistId(null);
          setCreatedCarEhPosEvento(false);
        }}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
      />
      {/* at-65 (BUG-V43): montagem condicional + filtros em vigor. Esta tela é a dona do
          slice global de filtros (as outras passaram a usar estado local, BUG-V32). */}
      {filterModalOpen && (
        <FilterCarModal
          onCancel={() => setFilterModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          availableClasses={availableClasses}
          sidebarRef={sidebarRef}
          isSidebarOpen={isStatusSidebarOpen}
          filtrosAplicados={{
            carClass: (selectedClasses as unknown as CarClass[]) || [],
            carStatus: (selectedStatuses as string[]) || [],
            pendingStatus: (selectedPendingStatuses as string[]) || [],
          }}
        />
      )}
      <StartChecklistModal
        isOpen={isStartChecklistModalOpen}
        onClose={() => {
          setIsStartChecklistModalOpen(false);
          setChecklistCarId(null);
          setChecklistReadOnly(false);
          setChecklistEhPosEvento(false);
        }}
        familia={checklistFamilia}
        ehPosEvento={checklistEhPosEvento}
        sidebarRef={sidebarRef}
        isSidebarOpen={isStatusSidebarOpen}
        onConfirm={(tipo) => {
          if (!checklistCarId) return;
          setIsStartChecklistModalOpen(false);
          setChecklistEhPosEvento(false);
          void requestStartChecklist(checklistCarId, tipo);
        }}
      />
      <ConfirmModal
        isOpen={Boolean(startChecklistConfirm)}
        message={`Tem certeza de que deseja dar início ao checklist de ${
          startChecklistConfirm ? checklistTypeLabel[startChecklistConfirm.tipo] || startChecklistConfirm.tipo : ''
        } ao veículo ${startChecklistConfirm?.plate || 'selecionado'}?`}
        confirmLabel="Iniciar"
        onConfirm={confirmStartChecklist}
        onCancel={() => setStartChecklistConfirm(null)}
      />
      <ChecklistModal
        isOpen={isChecklistModalOpen}
        onClose={() => {
          setIsChecklistModalOpen(false);
          setChecklistCarId(null);
          setChecklistSessionId(null);
          setChecklistReadOnly(false);
          // Recarrega os flags para os botões Fazer/Visualizar refletirem o checklist recém-concluído
          setChecklistFlagsRefreshKey((k) => k + 1);
          setRascunhosVersao((v) => v + 1);
        }}
        carId={checklistCarId || ''}
        sessionId={checklistSessionId}
        tipoServico={checklistTipoServico}
        readOnly={checklistReadOnly}
      />
      <RecepcaoActionsModal
        isOpen={actionsModalOpen}
        deliveredMode={isTi}
        carId={selectedCarForActions}
        onCancel={() => {
          setActionsModalOpen(false);
          setSelectedCarForActions(null);
        }}
        onEditCar={(id) => {
          setActionsModalOpen(false);
          handleOpenEditModal(id);
        }}
        onChangeStatus={(id) => {
          setActionsModalOpen(false);
          handleOpenStatusModal(id);
        }}
        onViewChecklist={(id, type) => {
          setActionsModalOpen(false);
          // at-96: a sessão é buscada POR TIPO, e num pós-evento o tipo real é
          // POS_EVENTO_ENTRADA/POS_EVENTO_SAIDA — pedir o tipo genérico não acharia nada e a
          // tela diria "checklist não encontrado" sobre um checklist que existe.
          const flags = checklistFlagsByCarId[id];
          const tipoReal = type === 'ENTRADA' ? flags?.entradaType : flags?.saidaType;
          openChecklistReadOnly(id, tipoReal || type);
        }}
        onStartChecklist={(id, _type, ehPosEvento) => {
          setActionsModalOpen(false);
          setChecklistCarId(id);
          setChecklistSessionId(null);
          setChecklistReadOnly(false);
          // at-96: o tipo é derivado pelo modal a partir da família e do atendimento.
          setChecklistFamilia('ENTRADA');
          setChecklistEhPosEvento(ehPosEvento);
          setIsStartChecklistModalOpen(true);
        }}
        onStartChecklistSaida={(id, ehPosEvento) => {
          setActionsModalOpen(false);
          void requestStartChecklist(id, tipoDeSaidaDoAtendimento(ehPosEvento));
        }}
        /* at-126: o TI não recebe a ação (ADR 0012) — mesmo padrão de `onEditarMotivo`. */
        onCompartilharAcompanhamento={!isTi && acompanhamentoAtivo ? handleCompartilharAcompanhamento : undefined}
        compartilhandoAcompanhamento={compartilhandoAcompanhamento}
        /* at-89: o TI não recebe a ação (é escrita — ADR 0012), no mesmo padrão de
           `onEnviarPesquisa`/`onEditarEntregue`: quem decide é o chamador. */
        onEditarMotivo={
          isTi
            ? undefined
            : (id) => {
                setActionsModalOpen(false);
                setCarroParaMotivo(id);
              }
        }
        hasEntradaCompleted={
          selectedCarForActions
            ? (checklistFlagsByCarId[selectedCarForActions] || { hasEntradaCompleted: false }).hasEntradaCompleted
            : false
        }
        hasSaidaCompleted={
          selectedCarForActions
            ? (checklistFlagsByCarId[selectedCarForActions] || { hasSaidaCompleted: false }).hasSaidaCompleted
            : false
        }
        sidebarRef={sidebarRef}
      />

      {/* at-89: edição do motivo da pendência da esteira atual. */}
      <MotivoEsteiraModal
        isOpen={Boolean(carroParaMotivo)}
        carId={carroParaMotivo}
        esteiraAtual={cars.find((c: any) => c?.id === carroParaMotivo)?.status ?? null}
        onCancel={() => setCarroParaMotivo(null)}
        onSaved={() => loadCars(currentPage, limit)}
        sidebarRef={sidebarRef}
      />

      <FillFormModal
        isOpen={!!activeTriggerTemplate}
        template={activeTriggerTemplate}
        preselectedCar={activeTriggerCar}
        onClose={() => {
          setActiveTriggerTemplate(null);
          setActiveTriggerCar(null);
        }}
        onSubmitted={() => {
          setActiveTriggerTemplate(null);
          setActiveTriggerCar(null);
        }}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Recepção"
        secoes={helpSections}
      />

    </div>
  );
};

export default Recepcao;
